import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Company } from '../../models/user.model';
import { createDefaultCart } from '../../utilities/type-defaults.util';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Quotation } from '../../models/quotation.model';
import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { UserService } from '../user/user.service';


export interface AddToCart {
  cart_items: CartItem[];
  seller_company?: string;
}
export interface UpdateCart {
  cart_items: CartItem[];
  seller_company?: string;
  cartId?: string;
}

export interface CreateQuotation {
  cart: Cart;
  quotation: Quotation;
}

export interface Cart {
  id: string;
  cart_items: CartItem[];
  seller_company: Company;
  buyer_company: Company;
  total_items: number;
  gst_amount: number;
  subtotal: number;
  discount_amount: number;
  discount_percentage: number;
  cart_total: number;
  total_savings: number;
  cashback_points: number;
  is_active: boolean;
  is_current: boolean;
  is_archived: boolean;
  initialized_by_admin: boolean;
  has_new_version: boolean;
  tax: any;
}

export interface CartItem {
  id?: string;
  product?: any;
  effective_price?: number;
  quantity?: number;
  parent_cart_item?: any,
  price?: number;
  discount_amount?: number;
  discount_percentage?: number;
  total?: number;
  gst?: any;
  tax?: string;
  is_product_offer_item?: boolean;
  offer_applied?: OfferDetails;
  product_discount_offer?: OfferDetails;
  featured_images?: any;
  cashback_points?: number;
  is_price_changed?: boolean;
  bid_price?: number;
  is_bidding_offer_item?: boolean;
  is_price_or_offer_changed?: boolean;
}

export interface OfferDetails {
  id: string
  name: string
  description: string
  offer_type: string
}


@Injectable({
  providedIn: 'root'
})
export class CartApiService {
  private userService = inject(UserService);
  private http = inject(HttpClient);
  private platformService = inject(PlatformService);
  private environmentService = inject(EnvironmentService);

  url: string;

  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  // Signal-based cart data
  private _cart = signal<Cart | null>(null);
  readonly cart = this._cart.asReadonly();



  constructor() {
    this.url = environment.baseUrl;
  }

  // Signal-based methods that return Observables for proper async handling
  addProductToCart(reqObj: AddToCart): Observable<Cart> {
    this._loading.set(true);
    const _environment = this.environmentService.environment();
    const currentUser = this.userService.appUser;

    // Check if user and company are available before making the request
    if (!currentUser?.company?.id) {
      // Return empty cart if user or company information not available
      this._loading.set(false);
      return of(createDefaultCart());
    }

    if (!_environment) {
      this._loading.set(false);
      return of(createDefaultCart());
    }

    // Merge the request object with seller_company
    const requestWithSellerCompany = {
      ...reqObj,
      seller_company: _environment?.sellerId ?? ''
    };

    return this.http.post<Cart>(`${this.url}/consumer/companies/${currentUser.company.id}/carts`, requestWithSellerCompany).pipe(
      tap({
        next: (cart: Cart) => {
          this._cart.set(cart);
          this._loading.set(false);
        },
        error: () => {
          this._loading.set(false);
        }
      })
    );
  }

  updateProductCart(reqObj: UpdateCart): Observable<Cart> {
    this._loading.set(true);
    const _environment = this.environmentService.environment();
    const currentUser = this.userService.appUser;

    // Check if user and company are available before making the request
    if (!currentUser?.company?.id) {
      this._loading.set(false);
      return of(createDefaultCart());
    }

    if (!_environment) {
      this._loading.set(false);
      return of(createDefaultCart());
    }

    if (!reqObj?.cartId) {
      this._loading.set(false);
      return of(createDefaultCart());
    }

    // Merge the request object with seller_company
    const requestWithSellerCompany = {
      ...reqObj,
      seller_company: _environment?.sellerId ?? ''
    };

    return this.http.patch<Cart>(`${this.url}/consumer/carts/${reqObj?.cartId}`, requestWithSellerCompany).pipe(
      tap({
        next: (cart: Cart) => {
          this._cart.set(cart);
          this._loading.set(false);
        },
        error: () => {
          this._loading.set(false);
        }
      })
    );
  }

  removeProductFromCart(cartItemId: string): Observable<Cart> {
    this._loading.set(true);
    return this.http.post<Cart>(`${this.url}/consumer/cart_items/${cartItemId}/remove/`, {}).pipe(
      tap({
        next: (cart: Cart) => {
          this._cart.set(cart);
          this._loading.set(false);
        },
        error: () => {
          this._loading.set(false);
        }
      })
    );
  }

  initiateQuotation(cartId: string): Observable<CreateQuotation> {
    this._loading.set(true);
    return this.http.post<CreateQuotation>(`${this.url}/consumer/carts/${cartId}/initiate_quotation/`, {}).pipe(
      tap({
        next: (response: CreateQuotation) => {
          this._cart.set(response?.cart);
          this._loading.set(false);
        },
        error: () => {
          this._loading.set(false);
        }
      })
    );
  }

  fetchCart(): Observable<Cart | null> {
    if (!this.platformService.isBrowser()) {
      this._loading.set(true);
      this._cart.set(null);
      // this._loading.set(false);
      return of(null);
    }

    const currentUser = this.userService.appUser;

    if (currentUser?.company?.id) {
      this._loading.set(true);
      const _environment = this.environmentService.environment();

      if (!_environment) {
        this._loading.set(false);
        return of(null as any);
      }

      const apiUrl = `${this.url}/consumer/companies/${currentUser?.company?.id}/carts`;

      return this.http.get<Cart>(apiUrl, {
        params: {
          seller: _environment?.sellerId ?? ''
        }
      }).pipe(
        map((response: any) => response.results),
        map((carts: any) => {
          if (Array.isArray(carts) && carts.length) {
            return carts[0];
          }
          return null;
        }),
        tap({
          next: (cart: Cart | null) => {
            this._cart.set(cart);
            this._loading.set(false);
          },
          error: () => {
            this._loading.set(false);
          }
        })
      );
    } else {
      this._loading.set(false);
      this._cart.set(null);
      return of(null);
    }
  }
}
